import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useFullHeightLayout } from '../../components/layout/AppShell';
import type { InboxThreadFilters } from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';
import {
  KIND_SEGMENTS,
  loadAmFilter,
  loadKindSegment,
  loadSortDir,
  loadStatusFilter,
  saveAmFilter,
  saveKindSegment,
  saveSortDir,
  saveStatusFilter,
  type FeedSortDir,
  type KindSegmentValue,
} from '../../lib/inboxView';
import { listAccounts } from '../accounts/api';
import { useCurrentUser } from '../auth';
import {
  bulkResolveThreads,
  deleteComment,
  fetchThreadDetail,
  fetchThreads,
  hideComment,
  setThreadStatus,
} from './api';
import { commentBodyMessage, DELETE_COMMENT_CONFIRM } from '../../lib/inboxRowActions';
import { pickAccountLinks } from '../../lib/accountLinks';
import { FilterRail } from './FilterRail';
import { AccountWorklistPanel } from './AccountWorklistPanel';
import { ThreadList } from './ThreadList';
import { ThreadDetail } from './ThreadDetail';
import { useWebhookHealthState } from './WebhookHealthCard';
import { ManageAccountsDialog } from './ManageAccountsDialog';
import { BulkResolveDialog } from './BulkResolveDialog';
import { Button } from '../../components/ui/button';

// Community Inbox — /community. Phase 1.5: fluid full-width three-pane layout
// (filter rail · thread list · detail, detail takes the largest share), a
// Comments/Reviews/Messages kind control, and a working-queue default of
// New + Open + Pending (session-persisted). List + open thread poll ~15s.

const PAGE_SIZE = 50;

function initialFilters(): InboxThreadFilters {
  const statuses = loadStatusFilter();
  const accountManagerIds = loadAmFilter();
  return {
    ...(statuses.length > 0 ? { statuses } : {}),
    ...(accountManagerIds.length > 0 ? { accountManagerIds } : {}),
  };
}

export function CommunityPage() {
  // Bound the shell to one viewport for as long as this page is mounted, so no
  // ancestor grows with content and each column can scroll independently.
  useFullHeightLayout();
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  // Drives the amber "needs attention" dot on the Manage accounts button.
  const webhookHealth = useWebhookHealthState();
  const [filters, setFilters] = useState<InboxThreadFilters>(initialFilters);
  const [kind, setKind] = useState<KindSegmentValue>(loadKindSegment);
  const [sortDir, setSortDir] = useState<FeedSortDir>(loadSortDir);
  const [pagesShown, setPagesShown] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const users = useQuery({
    queryKey: ['staff-users'],
    queryFn: async () => (await api<{ users: CurrentUser[] }>('/api/users')).users,
  });

  // "All" applies no kind filter (merged list of every kind, brief §4); the
  // individual tabs scope to one kind.
  const effectiveFilters = useMemo(
    () => (kind === 'all' ? { ...filters } : { ...filters, kinds: [kind] }),
    [filters, kind],
  );

  // "Load more" grows the page size (page stays 1) so newly arriving threads
  // never shift rows out of view between polls.
  const threads = useQuery({
    queryKey: ['community-threads', effectiveFilters, pagesShown, sortDir],
    queryFn: () => fetchThreads(effectiveFilters, 1, Math.min(pagesShown * PAGE_SIZE, 100), sortDir),
    refetchInterval: 15_000,
    placeholderData: (prev) => prev,
  });

  const onToggleSort = () => {
    setSortDir((prev) => {
      const next = prev === 'newest' ? 'oldest' : 'newest';
      saveSortDir(next);
      return next;
    });
    setPagesShown(1);
  };

  // Part D — "resolve all showing": resolves everything matching the CURRENT
  // filters (incl. the kind segment), not just the loaded page.
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const bulkResolve = useMutation({
    mutationFn: () => bulkResolveThreads(effectiveFilters),
    onSuccess: ({ resolved, capped }) => {
      setBulkResolveOpen(false);
      toast.success(
        capped
          ? `Resolved ${resolved} items — run it again to clear the rest.`
          : `Resolved ${resolved} item${resolved === 1 ? '' : 's'}.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['community-threads'] });
      void queryClient.invalidateQueries({ queryKey: ['community-thread'] });
      void queryClient.invalidateQueries({ queryKey: ['community-worklist'] });
      void queryClient.invalidateQueries({ queryKey: ['community-bulk-resolve-preview'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Row quick Hide / Delete (2026-09-30). The row holds only the thread, so the
  // target message is read from the thread's detail and chosen by the SAME
  // commentBodyMessage the detail pane uses. ⚠️ The id is passed EXPLICITLY:
  // the server's fallback with no messageId picks the LATEST incoming comment,
  // which is not what the pane targets, so omitting it could hide a different
  // comment from the one the pane would.
  const quickModerate = useMutation({
    mutationFn: async ({ threadId, action }: { threadId: string; action: 'hide' | 'delete' }) => {
      const detail = await fetchThreadDetail(threadId);
      const body = commentBodyMessage(detail.messages);
      if (!body) throw new Error('Could not find this comment. Open it to act on it.');
      if (action === 'hide') {
        if (body.hidden === true) return 'already-hidden' as const;
        await hideComment(threadId, body.id);
        return 'hidden' as const;
      }
      await deleteComment(threadId, body.id);
      return 'deleted' as const;
    },
    onSuccess: (outcome) => {
      toast.success(
        outcome === 'already-hidden'
          ? 'That comment is already hidden.'
          : outcome === 'hidden'
            ? 'Comment hidden.'
            : 'Comment deleted.',
      );
      void queryClient.invalidateQueries({ queryKey: ['community-threads'] });
      void queryClient.invalidateQueries({ queryKey: ['community-thread'] });
      void queryClient.invalidateQueries({ queryKey: ['community-post-group'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const quickResolve = useMutation({
    mutationFn: (threadId: string) => setThreadStatus(threadId, 'resolved'),
    onSuccess: () => {
      toast.success('Thread resolved.');
      void queryClient.invalidateQueries({ queryKey: ['community-threads'] });
      void queryClient.invalidateQueries({ queryKey: ['community-thread'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const accountNameById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a.name])),
    [accounts.data],
  );
  // The external link icons in the thread header (2026-09-30). This page
  // already holds the full Account list, so no projection change is needed here.
  const accountLinksById = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, pickAccountLinks(a)])),
    [accounts.data],
  );
  const usersByUid = useMemo(
    () => new Map((users.data ?? []).map((u) => [u.uid, u])),
    [users.data],
  );

  const onFiltersChange = (next: InboxThreadFilters) => {
    setFilters(next);
    saveStatusFilter(next.statuses ?? []);
    saveAmFilter(next.accountManagerIds ?? []);
    setPagesShown(1);
  };

  // The worklist panel drives the account filter: exactly-one selected account
  // is the "active" account; clicking it again clears the selection.
  const selectedAccountId =
    (filters.accountIds ?? []).length === 1 ? filters.accountIds![0]! : null;
  const onSelectAccount = (accountId: string) => {
    const isSole = selectedAccountId === accountId;
    onFiltersChange({ ...filters, accountIds: isSole ? undefined : [accountId] });
  };

  const activeSegment = KIND_SEGMENTS.find((s) => s.kind === kind) ?? KIND_SEGMENTS[0]!;

  return (
    // VIEWPORT-BOUNDED SHELL (sticky-composer fix, 2026-07-18). h-full resolves
    // against the opted-in AppShell's bounded `main`; min-h-0 lets the column
    // row shrink instead of being sized by its (very tall) content. Nothing
    // here may scroll at page level — each column scrolls itself, which is what
    // keeps the detail composer pinned to the SCREEN bottom rather than being
    // pushed below the fold by a long accounts list or comment feed.
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0">
        <PageHeader
          title="Community"
          description="Unified social inbox — comments, reviews and messages across Facebook, Instagram, Google and ads."
          actions={
            me.role === 'admin' ? (
              // Part F — the webhook health card moved INTO this dialog. The
              // amber dot keeps a dead webhook (which silently stops the whole
              // inbox) discoverable without costing a permanent page row.
              <Button
                variant="outline"
                size="sm"
                onClick={() => setManageOpen(true)}
                title={
                  webhookHealth.healthy
                    ? 'Manage community accounts'
                    : 'Manage community accounts — the Zernio webhook needs attention'
                }
              >
                Manage accounts
                {!webhookHealth.loading && !webhookHealth.healthy && (
                  <span
                    aria-label="Webhook needs attention"
                    className="ml-1.5 h-2 w-2 rounded-full bg-amber-500"
                  />
                )}
              </Button>
            ) : undefined
          }
        />
      </div>
      {me.role === 'admin' && (
        <ManageAccountsDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          accounts={accounts.data ?? []}
        />
      )}
      {/* The three-column row takes whatever height is left (flex-1) — no
          hard-coded calc(100vh - 200px), which under-/over-shot whenever the
          chrome above it changed height and let the row's bottom fall below
          the fold. */}
      <div className="flex min-h-0 flex-1 gap-4">
        {/* Part D1 — left rail widened (w-60 → w-72) so the filter dropdown
            popups sit inside the rail instead of spilling under the thread list,
            and so the accounts panel shows full names. It now gets the FULL
            column height and scrolls internally only if the accounts list
            exceeds a viewport — the "never truncate" ask, without the list
            dragging the page height with it. */}
        <div className="scroll-subtle flex w-72 min-h-0 shrink-0 flex-col gap-4 overflow-y-auto pb-2 pr-1">
          <FilterRail
            filters={filters}
            onChange={onFiltersChange}
            accounts={accounts.data ?? []}
            users={users.data ?? []}
            kind={kind}
          />
          <AccountWorklistPanel
            filters={filters}
            selectedAccountId={selectedAccountId}
            onSelectAccount={onSelectAccount}
          />
        </div>
        {/* Brief §5 — thread list widened from 26/30rem to 28/34rem, borrowed
            from the detail pane (still flex-1). The 4-tab control + richer rows
            get room; the detail stays comfortable for its xl+ two-column
            PostPreviewPanel (fluid full-width layout gives it the rest). */}
        <div className="flex min-h-0 w-[28rem] shrink-0 flex-col gap-2 xl:w-[34rem]">
          {/* Kind segmented control (Phase 1.5 §A). Reviews/DMs light up in Phase 2. */}
          <div className="flex shrink-0 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            {KIND_SEGMENTS.map((segment) => (
              <button
                key={segment.kind}
                type="button"
                onClick={() => {
                  setKind(segment.kind);
                  saveKindSegment(segment.kind);
                  setPagesShown(1);
                  // Review-only facets don't apply to other kinds — clear them
                  // when leaving the Reviews tab so they don't linger.
                  if (segment.kind !== 'review') {
                    setFilters((f) =>
                      f.minRating || f.maxRating || f.hasReply !== undefined
                        ? { ...f, minRating: undefined, maxRating: undefined, hasReply: undefined }
                        : f,
                    );
                  }
                }}
                className={cn(
                  'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  kind === segment.kind
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800',
                )}
              >
                {segment.label}
              </button>
            ))}
          </div>
          <ThreadList
            result={threads.data}
            selectedId={selectedId}
            onSelect={(t) => setSelectedId(t.id)}
            onLoadMore={() => setPagesShown((p) => p + 1)}
            onQuickResolve={(t) => quickResolve.mutate(t.id)}
            onQuickHide={(t) => quickModerate.mutate({ threadId: t.id, action: 'hide' })}
            onQuickDelete={(t) => {
              // Exactly as the detail pane: the same confirm, then delete.
              if (window.confirm(DELETE_COMMENT_CONFIRM)) {
                quickModerate.mutate({ threadId: t.id, action: 'delete' });
              }
            }}
            accountNameById={accountNameById}
            usersByUid={usersByUid}
            loading={threads.isLoading}
            emptyState={activeSegment.emptyState}
            sortDir={sortDir}
            onToggleSort={onToggleSort}
            onBulkResolve={() => setBulkResolveOpen(true)}
          />
          <BulkResolveDialog
            open={bulkResolveOpen}
            onOpenChange={setBulkResolveOpen}
            filters={effectiveFilters}
            accountNameById={accountNameById}
            onConfirm={() => bulkResolve.mutate()}
            resolving={bulkResolve.isPending}
          />
        </div>
        {selectedId ? (
          <ThreadDetail
            threadId={selectedId}
            users={users.data ?? []}
            accountNameById={accountNameById}
            accountLinksById={accountLinksById}
            onSelectThread={setSelectedId}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-slate-200 text-sm text-slate-400">
            Select a thread to view the conversation.
          </div>
        )}
      </div>
    </div>
  );
}
